<script setup>
import { defineProps, defineEmits } from 'vue'

defineProps({
  selectedElementLabel: { type: String, default: '' },
  widthPx: { type: String, default: '' },
  heightPx: { type: String, default: '' },
  fontSizePx: { type: String, default: '' },
  opacityPercent: { type: String, default: '100' },
  borderRadius: { type: String, default: '0' },
  backgroundColor: { type: String, default: '#ffffff' },
  textColor: { type: String, default: '#111111' },
  textAlignMode: { type: String, default: 'none' },
  elementAlignMode: { type: String, default: 'none' },
  borderStyle: { type: String, default: 'solid' },
  marginTop: { type: String, default: '0' },
  marginRight: { type: String, default: '0' },
  marginBottom: { type: String, default: '0' },
  marginLeft: { type: String, default: '0' },
  paddingTop: { type: String, default: '0' },
  paddingRight: { type: String, default: '0' },
  paddingBottom: { type: String, default: '0' },
  paddingLeft: { type: String, default: '0' },
  borderTopWidth: { type: String, default: '0' },
  borderRightWidth: { type: String, default: '0' },
  borderBottomWidth: { type: String, default: '0' },
  borderLeftWidth: { type: String, default: '0' },
  borderTopColor: { type: String, default: '#111111' },
  borderRightColor: { type: String, default: '#111111' },
  borderBottomColor: { type: String, default: '#111111' },
  borderLeftColor: { type: String, default: '#111111' },
})

const emit = defineEmits([
  'update:widthPx',
  'update:heightPx',
  'update:fontSizePx',
  'update:opacityPercent',
  'update:borderRadius',
  'update:backgroundColor',
  'update:textColor',
  'update:borderStyle',
  'update:marginTop',
  'update:marginRight',
  'update:marginBottom',
  'update:marginLeft',
  'update:paddingTop',
  'update:paddingRight',
  'update:paddingBottom',
  'update:paddingLeft',
  'update:borderTopWidth',
  'update:borderRightWidth',
  'update:borderBottomWidth',
  'update:borderLeftWidth',
  'update:borderTopColor',
  'update:borderRightColor',
  'update:borderBottomColor',
  'update:borderLeftColor',
  'apply-layout',
  'apply-spacing',
  'apply-appearance',
  'set-element-align',
  'set-text-align',
  'remove-element',
  'reset-selection',
])

function onInput(field, event) {
  emit(`update:${field}`, event.target.value)
}
</script>

<template>
  <aside class="inspector-panel">
    <div class="inspector-title">CSS-панель {{ selectedElementLabel || '' }}</div>
    <div v-if="selectedElementLabel" class="palette-body">
      <div class="palette-tip">Выбери элемент в дереве или на странице. Ниже — настройки внешнего вида.</div>
      <details class="accordion-item" open>
        <summary class="accordion-title">
          <span>Размер и положение</span>
          <i class="pi pi-angle-right accordion-chevron" />
        </summary>
        <div class="accordion-body">
          <div class="palette-grid2">
            <div>
              <label class="palette-label">Ширина (px)</label>
              <input :value="widthPx" type="number" min="0" class="palette-input" @input="onInput('widthPx', $event); emit('apply-layout')" />
            </div>
            <div>
              <label class="palette-label">Высота (px)</label>
              <input :value="heightPx" type="number" min="0" class="palette-input" @input="onInput('heightPx', $event); emit('apply-layout')" />
            </div>
          </div>
          <div>
            <label class="palette-label">Положение элемента по горизонтали</label>
            <div class="align-actions">
              <ButtonVue
                type="button"
                size="small"
                text
                icon="pi pi-ban"
                :class="{ 'align-btn-active': elementAlignMode === 'none' }"
                @click="emit('set-element-align', 'none')"
              />
              <ButtonVue
                type="button"
                size="small"
                text
                icon="pi pi-align-left"
                :class="{ 'align-btn-active': elementAlignMode === 'left' }"
                @click="emit('set-element-align', 'left')"
              />
              <ButtonVue
                type="button"
                size="small"
                text
                icon="pi pi-align-center"
                :class="{ 'align-btn-active': elementAlignMode === 'center' }"
                @click="emit('set-element-align', 'center')"
              />
              <ButtonVue
                type="button"
                size="small"
                text
                icon="pi pi-align-right"
                :class="{ 'align-btn-active': elementAlignMode === 'right' }"
                @click="emit('set-element-align', 'right')"
              />
            </div>
          </div>
        </div>
      </details>

      <details class="accordion-item">
        <summary class="accordion-title">
          <span>Отступы</span>
          <i class="pi pi-angle-right accordion-chevron" />
        </summary>
        <div class="accordion-body">
          <label class="palette-section">Внутренние (верх / право / низ / лево)</label>
          <div class="palette-grid4">
            <input :value="paddingTop" type="number" min="0" class="palette-input" @input="onInput('paddingTop', $event); emit('apply-spacing')" />
            <input :value="paddingRight" type="number" min="0" class="palette-input" @input="onInput('paddingRight', $event); emit('apply-spacing')" />
            <input :value="paddingBottom" type="number" min="0" class="palette-input" @input="onInput('paddingBottom', $event); emit('apply-spacing')" />
            <input :value="paddingLeft" type="number" min="0" class="palette-input" @input="onInput('paddingLeft', $event); emit('apply-spacing')" />
          </div>

          <label class="palette-section">Внешние (верх / право / низ / лево)</label>
          <div class="palette-grid4">
            <input :value="marginTop" type="number" min="0" class="palette-input" @input="onInput('marginTop', $event); emit('apply-spacing')" />
            <input :value="marginRight" type="number" min="0" class="palette-input" @input="onInput('marginRight', $event); emit('apply-spacing')" />
            <input :value="marginBottom" type="number" min="0" class="palette-input" @input="onInput('marginBottom', $event); emit('apply-spacing')" />
            <input :value="marginLeft" type="number" min="0" class="palette-input" @input="onInput('marginLeft', $event); emit('apply-spacing')" />
          </div>
        </div>
      </details>

      <details class="accordion-item">
        <summary class="accordion-title">
          <span>Рамка</span>
          <i class="pi pi-angle-right accordion-chevron" />
        </summary>
        <div class="accordion-body">
          <label class="palette-section">Стиль рамки</label>
          <select :value="borderStyle" class="palette-input" @change="onInput('borderStyle', $event); emit('apply-appearance')">
            <option value="solid">Сплошная</option>
            <option value="dashed">Пунктир</option>
            <option value="dotted">Точки</option>
            <option value="double">Двойная</option>
            <option value="none">Без рамки</option>
          </select>

          <label class="palette-section">Толщина рамки (верх / право / низ / лево)</label>
          <div class="palette-grid4">
            <input :value="borderTopWidth" type="number" min="0" class="palette-input" @input="onInput('borderTopWidth', $event); emit('apply-appearance')" />
            <input :value="borderRightWidth" type="number" min="0" class="palette-input" @input="onInput('borderRightWidth', $event); emit('apply-appearance')" />
            <input :value="borderBottomWidth" type="number" min="0" class="palette-input" @input="onInput('borderBottomWidth', $event); emit('apply-appearance')" />
            <input :value="borderLeftWidth" type="number" min="0" class="palette-input" @input="onInput('borderLeftWidth', $event); emit('apply-appearance')" />
          </div>

          <label class="palette-section">Цвет рамки (верх / право / низ / лево)</label>
          <div class="palette-grid4">
            <input :value="borderTopColor" type="color" class="palette-input palette-input--color" @input="onInput('borderTopColor', $event); emit('apply-appearance')" />
            <input :value="borderRightColor" type="color" class="palette-input palette-input--color" @input="onInput('borderRightColor', $event); emit('apply-appearance')" />
            <input :value="borderBottomColor" type="color" class="palette-input palette-input--color" @input="onInput('borderBottomColor', $event); emit('apply-appearance')" />
            <input :value="borderLeftColor" type="color" class="palette-input palette-input--color" @input="onInput('borderLeftColor', $event); emit('apply-appearance')" />
          </div>

          <div class="palette-grid2">
            <div>
              <label class="palette-label">Скругление углов (px)</label>
              <input :value="borderRadius" type="number" min="0" class="palette-input" @input="onInput('borderRadius', $event); emit('apply-appearance')" />
            </div>
            <div>
              <label class="palette-label">Прозрачность (%)</label>
              <input
                :value="opacityPercent"
                type="number"
                min="0"
                max="100"
                class="palette-input"
                @input="onInput('opacityPercent', $event); emit('apply-appearance')"
              />
            </div>
          </div>
        </div>
      </details>

      <details class="accordion-item">
        <summary class="accordion-title">
          <span>Типографика</span>
          <i class="pi pi-angle-right accordion-chevron" />
        </summary>
        <div class="accordion-body">
          <div class="palette-grid2">
            <div>
              <label class="palette-label">Цвет фона</label>
              <input
                :value="backgroundColor"
                type="color"
                class="palette-input palette-input--color"
                @input="onInput('backgroundColor', $event); emit('apply-appearance')"
              />
            </div>
            <div>
              <label class="palette-label">Цвет текста</label>
              <input :value="textColor" type="color" class="palette-input palette-input--color" @input="onInput('textColor', $event); emit('apply-appearance')" />
            </div>
          </div>
          <div>
            <label class="palette-label">Размер шрифта (px)</label>
            <input :value="fontSizePx" type="number" min="1" class="palette-input" @input="onInput('fontSizePx', $event); emit('apply-layout')" />
          </div>
          <div>
            <label class="palette-label">Выравнивание текста</label>
            <div class="align-actions">
              <ButtonVue
                type="button"
                size="small"
                text
                icon="pi pi-ban"
                :class="{ 'align-btn-active': textAlignMode === 'none' }"
                @click="emit('set-text-align', 'none')"
              />
              <ButtonVue
                type="button"
                size="small"
                text
                icon="pi pi-align-left"
                :class="{ 'align-btn-active': textAlignMode === 'left' }"
                @click="emit('set-text-align', 'left')"
              />
              <ButtonVue
                type="button"
                size="small"
                text
                icon="pi pi-align-center"
                :class="{ 'align-btn-active': textAlignMode === 'center' }"
                @click="emit('set-text-align', 'center')"
              />
              <ButtonVue
                type="button"
                size="small"
                text
                icon="pi pi-align-right"
                :class="{ 'align-btn-active': textAlignMode === 'right' }"
                @click="emit('set-text-align', 'right')"
              />
              <ButtonVue
                type="button"
                size="small"
                text
                icon="pi pi-align-justify"
                :class="{ 'align-btn-active': textAlignMode === 'justify' }"
                @click="emit('set-text-align', 'justify')"
              />
            </div>
          </div>
        </div>
      </details>

      <div class="palette-actions">
        <ButtonVue type="button" size="small" severity="danger" outlined label="Удалить" @click="emit('remove-element')" />
        <ButtonVue type="button" size="small" text label="Сброс выбора" @click="emit('reset-selection')" />
      </div>
    </div>
    <div v-else class="inspector-empty">
      Кликни по элементу в дереве или на странице (или ПКМ), чтобы открыть настройки.
    </div>
  </aside>
</template>

<style scoped>
.inspector-panel {
  border: 1px solid var(--surface-border, #d1d5db);
  border-radius: 10px;
  background: var(--surface-card, #ffffff);
  padding: 10px;
  height: 100%;
  overflow: auto;
  min-height: 0;
}

.inspector-title {
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 10px;
  color: var(--text-color, #111827);
}

.palette-body {
  display: grid;
  gap: 8px;
}

.palette-label {
  font-size: 12px;
  color: var(--text-color-secondary, #6b7280);
}

.palette-tip {
  font-size: 12px;
  color: var(--text-color-secondary, #6b7280);
  line-height: 1.35;
}

.accordion-item {
  border: 1px solid var(--surface-border, #d1d5db);
  border-radius: 8px;
  background: var(--surface-card, #ffffff);
}

.accordion-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  list-style: none;
  font-size: 12px;
  font-weight: 700;
  color: var(--text-color, #111827);
  padding: 8px 10px;
  user-select: none;
}

.accordion-title::-webkit-details-marker {
  display: none;
}

.accordion-chevron {
  font-size: 12px;
  transition: transform 0.18s ease;
}

.accordion-item[open] .accordion-chevron {
  transform: rotate(90deg);
}

.accordion-body {
  padding: 0 10px 10px;
  display: grid;
  gap: 8px;
}

.align-actions {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.align-btn-active {
  background: rgba(37, 99, 235, 0.12) !important;
}

.palette-section {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 700;
  color: var(--text-color, #111827);
}

.palette-grid2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.palette-grid4 {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}

.palette-input {
  width: 100%;
  border: 1px solid var(--surface-border, #d1d5db);
  border-radius: 6px;
  background: var(--surface-card, #fff);
  color: var(--text-color, #111827);
  min-height: 32px;
  padding: 6px 8px;
}

.palette-input--color {
  padding: 2px;
}

.palette-actions {
  margin-top: 6px;
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.inspector-empty {
  color: var(--text-color-secondary, #6b7280);
  font-size: 13px;
}
</style>
